import React, { useState } from 'react';
import {
  Tabs,
  Tab,
  Box,
  Button,
  IconButton,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useDispatch, useSelector } from '../../../state';
import { closeModal } from '../../../state/auditHistoryModalSlice';
import DraggablePaper from '../DraggablePaper';
import { useTranslation } from 'react-i18next';
import TimelineTab from './TimelineTab';
import ListViewTab from './ListViewTab';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

export interface ChangePassword {
  oldPassword: string;
  oldPasswordError: string;
  newPassword: string;
  newPasswordError: string;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

export default function AuditHistoryModal() {
  const { t: tMA } = useTranslation([], {
    keyPrefix: 'team.Modals.AuditHistory',
  });

  const { formData, isOpenAuditHistoryModal, loading, backToLabel } =
    useSelector(({ auditHistoryModalSlice }) => auditHistoryModalSlice);

  const [tab, setTab] = useState(0);

  const dispatch = useDispatch();

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTab(newValue);
  };

  const resetState = () => {
    setTab(0);
  };

  const handleClose = () => {
    dispatch(closeModal({ resetState: true }));
    resetState();
  };

  return (
    <Dialog
      open={isOpenAuditHistoryModal}
      onClose={(event, reason) => {
        if (reason && reason === 'backdropClick') return;
        handleClose();
      }}
      maxWidth="lg"
      scroll="paper"
      PaperProps={{ className: 'w-full' }}
      PaperComponent={DraggablePaper}
    >
      <DialogTitle
        id="draggable-dialog-title"
        sx={{ m: 0, p: 1, cursor: 'move' }}
      >
        {tMA('History')}
      </DialogTitle>
      <IconButton
        disabled={loading}
        color="primary"
        sx={{ position: 'absolute', right: 4, top: 4 }}
        onClick={handleClose}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent dividers sx={{ p: 1, minHeight: 550 }}>
        {loading ? (
          <Box
            height="400px"
            display="flex"
            justifyContent="center"
            alignItems="center"
          >
            <CircularProgress size={80} />
          </Box>
        ) : (
          <>
            <Container>
              <Tabs
                value={tab}
                onChange={handleTabChange}
                sx={{
                  '& .MuiTabs-flexContainer': {
                    justifyContent: 'start',
                  },
                }}
              >
                <Tab
                  sx={{ fontWeight: 'bold' }}
                  label={'Timeline'}
                  {...a11yProps(0)}
                />
                <Tab
                  sx={{ fontWeight: 'bold' }}
                  label={'List View'}
                  {...a11yProps(1)}
                />
                <Box sx={{ flexGrow: 1 }} />
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '2px',
                  }}
                >
                  <Button
                    disabled={loading}
                    variant="outlined"
                    color="primary"
                    onClick={handleClose}
                  >
                    {tMA('Back To') + ' ' + backToLabel}
                  </Button>
                </Box>
              </Tabs>
            </Container>
            <TabPanel value={tab} index={0}>
              <TimelineTab formData={formData} />
            </TabPanel>
            <TabPanel value={tab} index={1}>
              <ListViewTab formData={formData} />
            </TabPanel>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
